import {startCase} from "lodash";
import {NodeId, NodeType} from "../../../../model/Graph";
import {Playshot} from "../../../../model/Playshot";
import {makeAsyncIterable} from "../../../async-iterable/make-async-iterable";
import {CodyResponse, CodyResponseType} from "../../../Cody";
import {getNodeByIdOrFQCN} from "../../../cody-wizard/node/get-node-by-id-or-fqcn";
import {nodeLabel, removeDefaultService} from "../../default-service";
import {ApplyTask, runApplyTasks} from "../../run-apply-tasks";
import {
  PlayshotApplyContext,
  PlayshotChanged,
  PlayshotChangedDiff,
  PlayshotChangedOperation,
  PlayshotChangedType
} from "../PlayshotChanged";
import {PlayInformationRuntimeInfo} from "./information-definitions";
import {prepareDiffInformation} from "./prepare-diff-information";

export class InformationRemoved implements PlayshotChanged {
  public readonly name: string;
  public readonly type: PlayshotChangedType = 'InformationRemoved';
  public readonly operation: PlayshotChangedOperation = 'removed';
  public readonly nodeId: NodeId;
  public readonly nodeType = NodeType.document;
  public readonly diff: PlayshotChangedDiff;

  constructor(name: string, nodeId: string, information: PlayInformationRuntimeInfo, oldPlayshot: Playshot) {

    this.name = name;
    this.nodeId = nodeId;
    this.diff = {
      new: null,
      old: prepareDiffInformation(information, oldPlayshot),
    };
  }

  public async apply({syncContext, graph, playshot, addedNodes, elementsTree}: PlayshotApplyContext): Promise<AsyncIterable<CodyResponse>> {
    if(addedNodes[this.nodeId]) {
      return makeAsyncIterable({
        cody: ``,
        type: CodyResponseType.Empty
      })
    }

    const existingNode = getNodeByIdOrFQCN(this.nodeId, this.name, NodeType.document, graph, elementsTree);

    if(!existingNode) {
      return makeAsyncIterable({
        cody: `Hhm, I'm skipping the change "${startCase(this.type)}: ${removeDefaultService(this.name, playshot)}", because the information cannot be found on the board.`,
        details: `There is no element on the board with ID: ${this.nodeId}`,
        type: CodyResponseType.Warning
      })
    }

    const similarNodes = elementsTree.getSimilarElements(existingNode);

    const applyTasks: ApplyTask[] = [
      {
        name: "Focus information to remove",
        func: async () => {
          graph.focusNode(existingNode);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      },
      {
        name: "Remove information",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(existingNode);

              resolve({
                cody: `I've removed the information card for "${nodeLabel(this.name)}" from the board`
              })
            }, 1500);
          })
        }
      }
    ];

    similarNodes.forEach(sN => {
      applyTasks.push({
        name: "Focus information to remove",
        func: async () => {
          graph.focusNode(sN);
          return {
            cody: ``,
            type: CodyResponseType.Empty
          }
        }
      });
      applyTasks.push({
        name: "Remove information",
        func: async () => {
          return new Promise(resolve => {
            window.setTimeout(() => {
              graph.deleteNode(sN);

              resolve({
                cody: ``,
                type: CodyResponseType.Empty
              });
            }, 1500);
          })
        }
      })
    })

    return runApplyTasks(applyTasks)
  }
}
